import { Button } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody, ModalFooter } from "@vds/modals";
import { Body } from "@vds/typography";
import { lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { generate } from "shortid";
import { Header, SLButton, SLInput, SLDropDown } from "../../components/core/Controls";
import { GridComponent } from "../../components/core/Table/GridComponent";
import { CompactCol, FlexRow, LargeText, Spacer, SLGridLayout, SLRowLayout, SLNotification } from "../../components/core/Utility";
import { COLUMN_WIDTHS, OPEN_TAB_EVENT } from "../../constants/ApplicationConstants";
import { ACCOUNT_REGEX, ESN_REGEX } from "../../constants/ValidationConstants";
import { fullboxswap, salesforceCases, vtuLocalSwap, getVendorNameLookUp, getInstallerNameLookUp } from "../../services/VTU/swapDevices";
import { resetTab, updateAddress } from "../../utility/navigation";
import { messageBus } from "../../utility/messageBus";
import { CHECKIN_PAGE } from "../pageList";
import styled from "@emotion/styled";
import ConfirmationModal from "../../components/core/ConfirmModal";
import { TabContext } from "../../context/tabContext";

const ERROR_COLOR = "#d32f2f";
const NA_OPTION_LABEL = "N/A";
const NA_OPTION_SEARCH = NA_OPTION_LABEL.toLowerCase();

const NotificationWrapper = styled(SLNotification)`
  .ldhZEP {
    max-width: 15%;
  }
`;

const SLInputWrapper = styled(SLInput)`
  height: auto;
`;
const ButtonFlexWrapper = styled(FlexRow)`
  display: flex;
  justify-content: space-between;
  width: 80%;
  max-width: 38.75rem;
  min-width: 4.125rem;
`;

const SearchableSelectWrapper = styled.div`
  position: relative;
  margin: 20px 0px;
  min-width: 66px;
  max-width: 604px;
`;

const SearchableInputContainer = styled.div`
  display: flex;
  align-items: center;
  height: 44px;
  margin-top: 8px;
  border: 1px solid ${(props) => (props.hasError ? ERROR_COLOR : "#d8dada")};
  border-radius: 4px;
  background: ${(props) => (props.disabled ? "#f6f6f6" : "#fff")};
  padding: 0 44px 0 12px;
  position: relative;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "text")};

  &:hover {
    border-color: ${(props) => (props.hasError ? ERROR_COLOR : props.disabled ? "#d8dada" : "#000")};
  }

  &:focus-within {
    border-color: ${(props) => (props.hasError ? ERROR_COLOR : "#000")};
    border-width: 2px;
    padding: 0 43px 0 11px;
  }
`;

const SearchableInput = styled.input`
  width: 100%;
  appearance: none;
  border: none;
  padding: 0;
  font-size: 1rem;
  line-height: 1.5;
  outline: none;
  background: transparent;
  box-sizing: border-box;
  font-family: inherit;
  color: #000;
  height: 100%;

  &::placeholder {
    color: #6f7171;
  }

  &:disabled {
    cursor: not-allowed;
    color: #6f7171;
  }
`;

const SearchableArrow = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  pointer-events: none;
  display: flex;
  align-items: center;
  color: #000;
`;

const SearchableMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 20;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d8dada;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const SearchableItem = styled.div`
  padding: 10px 12px;
  font-size: 1rem;
  cursor: pointer;
  background: ${(props) => (props.active ? "#f6f6f6" : "#fff")};

  &:hover {
    background: #f6f6f6;
  }
`;

const SearchableEmpty = styled.div`
  padding: 10px 12px;
  color: #6f7171;
  font-size: 0.875rem;
  font-style: italic;
`;

const ErrorText = styled.span`
  color: ${ERROR_COLOR};
  font-size: 12px;
  font-weight: bold;
  margin-top: 4px;
  display: block;
`;

const RequiredIndicator = styled.span`
  color: ${ERROR_COLOR};
  margin-left: 2px;
`;

const renderDropdownMenu = ({ items, searchText, selectedName, nameKey, idKey, emptyMessage, onSelect }) => {
  const isNaSearch = NA_OPTION_SEARCH.startsWith(searchText.toLowerCase());
  const showNaOption = (!selectedName || searchText !== selectedName) && isNaSearch;

  return (
    <SearchableMenu>
      {showNaOption && (
        <SearchableItem
          key={`${idKey}-na`}
          active={selectedName === NA_OPTION_LABEL}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect({ [nameKey]: NA_OPTION_LABEL, [idKey]: idKey === "vendorId" ? null : "na" })}
        >
          {NA_OPTION_LABEL}
        </SearchableItem>
      )}
      {items.length === 0 && !isNaSearch ? (
        <SearchableEmpty>{emptyMessage}</SearchableEmpty>
      ) : (
        items.map((item, index) => (
          <SearchableItem
            key={`${item[idKey]}-${item[nameKey]}-${index}`}
            active={item[nameKey] === selectedName}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(item)}
          >
            {item[nameKey]}
          </SearchableItem>
        ))
      )}
    </SearchableMenu>
  );
};

const matchesSearchByTokens = (value, query) => {
  const normalizedValue = (value || "").toLowerCase();
  const normalizedQuery = (query || "").trim().toLowerCase();

  if (!normalizedQuery) return true;
  if (normalizedValue.startsWith(normalizedQuery)) return true;

  const valueParts = normalizedValue.split(/\s+/).filter(Boolean);
  const queryParts = normalizedQuery.split(/\s+/).filter(Boolean);

  return queryParts.every((queryPart) => valueParts.some((valuePart) => valuePart.startsWith(queryPart)));
};

const gridConfig = [
  {
    key: "CASE",
    value: "CaseNumber",
    isFilterEnabled: true,
    filterType: "String",
    isCustomFilter: false,
    isSortable: true,
    width: COLUMN_WIDTHS.medium,
  },
  {
    key: "STATUS",
    value: "Status",
    isFilterEnabled: true,
    filterType: "String",
    isCustomFilter: false,
    isSortable: true,
    width: COLUMN_WIDTHS.small,
  },
  {
    key: "Date Opened",
    value: "CreatedDate",
    isFilterEnabled: true,
    filterType: "String",
    isCustomFilter: false,
    isSortable: true,
    width: COLUMN_WIDTHS.medium,
  },
];

export default function SwapDevicesFinal({ esn, accountId, orderNumber, contractFlexFlag, detailsForESN }) {
  const [notification, setNotification] = useState();
  const [oldESN, setOldESN] = useState(esn);
  const [oldESNError, setOldESNError] = useState(false);
  const [newESN, setNewESN] = useState("");
  const [newESNError, setNewESNError] = useState(false);
  const [account, setAccount] = useState(accountId);
  const [accountError, setAccountError] = useState(false);
  const [order, setOrder] = useState(orderNumber);
  const [orderError, setOrderError] = useState(false);
  const [oldESNCases, setOldESNCases] = useState([]);
  const [newESNCases, setNewESNCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const [cases, setCases] = useState(detailsForESN.oldCaseList.caseList);
  const [oldESNNotification, setOldESNNotification] = useState(cases?.length > 0 ? false : true);
  const [newESNNotification, setNewESNNotification] = useState();
  const [reason, setReason] = useState("");
  const [installType, setInstallType] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [installTypeError, setInstallTypeError] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [vendorNameError, setVendorNameError] = useState(false);
  const [installerName, setInstallerName] = useState("");
  const [installerNameError, setInstallerNameError] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [installerList, setInstallerList] = useState([]);
  const [vendorSearchText, setVendorSearchText] = useState("");
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [installerSearchText, setInstallerSearchText] = useState("");
  const [installerDropdownOpen, setInstallerDropdownOpen] = useState(false);
  const [confirmation, setConfirmation] = useState();
  const [selectedCaseNo, setSelectedCaseNo] = useState(detailsForESN.oldCaseList.caseList?.[0]?.CaseNumber ?? "");
  const {
    swapDevicePage: { setSwapESN },
  } = useContext(TabContext);

  const vendorWrapperRef = useRef(null);
  const installerWrapperRef = useRef(null);
  const vendorBlurTimer = useRef(null);
  const installerBlurTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (vendorBlurTimer.current) clearTimeout(vendorBlurTimer.current);
      if (installerBlurTimer.current) clearTimeout(installerBlurTimer.current);
    };
  }, []);

  const handleVendorBlur = useCallback((e) => {
    if (vendorWrapperRef.current?.contains(e.relatedTarget)) return;
    vendorBlurTimer.current = setTimeout(() => setVendorDropdownOpen(false), 150);
  }, []);

  const handleInstallerBlur = useCallback((e) => {
    if (installerWrapperRef.current?.contains(e.relatedTarget)) return;
    installerBlurTimer.current = setTimeout(() => setInstallerDropdownOpen(false), 150);
  }, []);

  const filteredVendors = useMemo(
    () => vendorList.filter((v) => matchesSearchByTokens(v.vendorName, vendorSearchText)),
    [vendorList, vendorSearchText]
  );

  const filteredInstallers = useMemo(
    () => installerList.filter((i) => matchesSearchByTokens(i.installerName, installerSearchText)),
    [installerList, installerSearchText]
  );

  const openTab = (eachSubMenu, newESN) => {
    updateAddress(`/#${eachSubMenu.route}`);
    let tab = { tabLabel: eachSubMenu.name, componentName: eachSubMenu.id, url: eachSubMenu.route };
    resetTab();
    new Promise((resolve) => {
      setTimeout(() => resolve(true), 200);
    }).then(() => {
      messageBus.trigger(OPEN_TAB_EVENT, tab);
      setSwapESN(newESN);
    });
  };

  useEffect(() => {
    if (detailsForESN.warning) notify(detailsForESN.warning, "info");
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const vendorResponse = await getVendorNameLookUp();
        if (vendorResponse?.status === 200 && vendorResponse?.data?.data) {
          const seenVendors = new Set();
          const uniqueVendors = vendorResponse.data.data.filter((vendor) => {
            const dedupKey = `${vendor?.vendorId}-${vendor?.vendorName}`;
            if (seenVendors.has(dedupKey)) return false;
            seenVendors.add(dedupKey);
            return true;
          });
          setVendorList(uniqueVendors);
        }
      } catch (err) {
        notify("Failed to load vendor data. Please try again.", "error");
      }
    };
    fetchVendors();
  }, []);

  const handleVendorSelect = useCallback(
    async (vendor) => {
      if (vendorNameError) setVendorNameError(false);
      setVendorName(vendor.vendorName);
      setVendorSearchText(vendor.vendorName);
      setVendorDropdownOpen(false);
      setInstallerName("");
      setInstallerSearchText("");
      setInstallerList([]);
      if (vendor?.vendorId) {
        try {
          const installerResponse = await getInstallerNameLookUp(vendor.vendorId);
          if (installerResponse?.status === 200 && installerResponse?.data?.data) {
            setInstallerList(installerResponse.data.data);
          }
        } catch (err) {
          notify("Failed to load installer data. Please try again.", "error");
        }
      }
    },
    [vendorNameError]
  );

  const handleInstallerSelect = useCallback(
    (installer) => {
      if (installerNameError) setInstallerNameError(false);
      setInstallerName(installer.installerName);
      setInstallerSearchText(installer.installerName);
      setInstallerDropdownOpen(false);
    },
    [installerNameError]
  );

  const handleReasonChange = (e) => {
    if (reasonError) setReasonError(false);
    setOldESNNotification(false);
    setNewESNNotification(false);
    setReason(e.target.value);
  };

  const handleInstallTypeChange = (e) => {
    if (installTypeError) setInstallTypeError(false);
    setOldESNNotification(false);
    setNewESNNotification(false);
    setInstallType(e.target.value);
  };

  const oldESNChange = (e) => {
    if (oldESNError) setOldESNError(false);
    setOldESNNotification(false);
    setNewESNNotification(false);
    setOldESN(e.target.value);
  };

  const newESNChange = (e) => {
    if (newESNError) setNewESNError(false);
    setOldESNNotification(false);
    setNewESNNotification(false);
    setNewESN(e.target.value);
  };

  const accountChange = (e) => {
    if (accountError) setAccountError(false);
    setOldESNNotification(false);
    setNewESNNotification(false);
    setAccount(e.target.value);
  };
  const orderChange = (e) => {
    if (orderError) setOrderError(false);
    setOrder(e.target.value);
    setOldESNNotification(false);
    setNewESNNotification(false);
  };
  const notify = (content, type) => {
    setNotification(<NotificationWrapper key={generate()} type={type} title={content} fullBleed={true} />);
  };

  const validate = () => {
    let flag = false;
    let oldEsn = oldESN.trim();
    let newEsn = newESN.trim();
    if (!oldEsn.match(ESN_REGEX)) {
      setOldESNError(true);
      flag = true;
    }
    if (!newEsn.match(ESN_REGEX)) {
      setNewESNError(true);
      flag = true;
    }
    if (!account.match(ACCOUNT_REGEX)) {
      setAccountError(true);
      flag = true;
    }
    if (contractFlexFlag && reason === "") {
      setReasonError(true);
      flag = true;
    }
    if (contractFlexFlag && installType === "") {
      setInstallTypeError(true);
      flag = true;
    }
    if (vendorName === "") {
      setVendorNameError(true);
      flag = true;
    }
    if (installerName === "") {
      setInstallerNameError(true);
      flag = true;
    }

    return flag;
  };
  const swapESN = async () => {
    if (cases?.length > 0) setCases([]);
    if (oldESNCases.length > 0) setOldESNCases([]);
    if (newESNCases.length > 0) setNewESNCases([]);
    setOldESNNotification(false);
    setNewESNNotification(false);
    if (validate()) {
      return;
    }
    let newEsn = newESN.trim()
    setLoading(true);
    let payload = {
      oldESN: oldESN,
      newESN: newEsn,
      universalAccountId: account,
      orderNumber: order,
      reason: reason,
      installType: installType,
      cfOrder: contractFlexFlag,
      installerName: installerName,
      caseNo: selectedCaseNo,
    };
    const response = await fullboxswap(payload);
    const { status, data } = response;
    if (status === 200 && data) {
      if (response?.data?.statusCode !== "200") {
        notify(response?.data?.message || "Couldn't swap ESN", "error");
      } else {
        setConfirmation({
          headerText: "Success",
          bodyText: data.data,
          onContinue: () => {
            openTab(CHECKIN_PAGE, newESN);
          },
        });
      }
    } else {
      notify(response?.response?.data?.message, "error");
    }
    setLoading(false);
  };

  const searchSalesforceCases = async () => {
    if (cases?.length > 0) setCases([]);
    if (oldESNCases.length > 0) setOldESNCases([]);
    if (newESNCases.length > 0) setNewESNCases([]);
    setOldESNNotification(false);
    setNewESNNotification(false);
    if (validate()) {
      return;
    }
    setLoading(true);
    let newESNData = await salesforceCases(newESN);
    let oldESNData = await salesforceCases(oldESN);
    if (oldESNData.status === 200 && oldESNData?.data?.data?.caseList) {
      oldESNData = oldESNData.data.data.caseList;
      setOldESNCases(oldESNData);
      setSelectedCaseNo(oldESNData[0]?.CaseNumber ?? "");
      setOldESNNotification(false);
    } else {
      setOldESNNotification(true);
    }
    if (newESNData.status === 200 && newESNData?.data?.data?.caseList) {
      newESNData = newESNData.data.data.caseList;
      setNewESNCases(newESNData);
      setNewESNNotification(false);
    } else {
      setNewESNNotification(true);
    }
    setLoading(false);
  };

  const localSwap = async () => {
    if (validate()) {
      return;
    }
    let newEsn = newESN.trim();
    let oldEsn = oldESN.trim();
    setLoading(true);
    let response = await vtuLocalSwap(oldEsn, newEsn);
    if (response.status === 200) {
      notify("ESNs successfully swapped", "success");
    } else {
      setAlertModal(true);
    }
    setLoading(false);
  };
  return (
    <>
      <Header>Swap Devices</Header>
      {notification}
      <Loader active={loading} />
      <ConfirmationModal
        isOpen={confirmation}
        bodyText={confirmation?.bodyText}
        headerText={confirmation?.headerText}
        onContinue={() => {
          confirmation?.onContinue();
          setConfirmation(null);
        }}
      />
      <Modal opened={alertModal} disableOutsideClick closeButton={null}>
        <ModalBody>
          <Body size="large" bold>
            We are currently experiencing difficulty in processing your request. Please try again sometime.
          </Body>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setAlertModal(false)}>OK</Button>
        </ModalFooter>
      </Modal>
      <SLGridLayout colSizes={{ mobile: 4, tablet: 6, desktop: 6 }}>
        <FlexRow>
          <LargeText bold={true} css={{ padding: "10px 0px" }}>
            Input the new device&apos;s ESN
          </LargeText>
        </FlexRow>
        <SLRowLayout>
          <CompactCol css={{ paddingLeft: "10px !important" }}>
            <SLInputWrapper
              value={oldESN}
              onChange={oldESNChange}
              type="text"
              label="Old ESN"
              width="80%"
              error={oldESNError}
              errorText="Provide a valid ESN"
            />
            {detailsForESN?.oldEsnLable && <> Label: {detailsForESN.oldEsnLable} </>}
            <SLInputWrapper
              value={account}
              onChange={accountChange}
              type="text"
              label="Account#"
              width="80%"
              error={accountError}
              errorText="Please enter correct account number"
            />
            {detailsForESN?.accountName && <> {detailsForESN.accountName} </>}
            <SLInputWrapper
              value={order}
              onChange={orderChange}
              type="text"
              label="Order#"
              width="80%"
              error={orderError}
              errorText="Please enter correct order number"
            />
            <SLInputWrapper
              value={newESN}
              onChange={newESNChange}
              type="text"
              label="New ESN#"
              width="80%"
              error={newESNError}
              errorText="Provide a valid ESN"
            />
            <SearchableSelectWrapper ref={vendorWrapperRef}>
              <Body size="large" primitive="span" bold css={{ marginBottom: "4px", display: "block" }}>
                Vendor Name<RequiredIndicator aria-hidden="true">*</RequiredIndicator>
              </Body>
              <SearchableInputContainer hasError={vendorNameError}>
                <SearchableInput
                  type="text"
                  data-testid="vendor-name"
                  aria-required="true"
                  placeholder="Type to search vendor..."
                  value={vendorSearchText}
                  onFocus={() => setVendorDropdownOpen(true)}
                  onBlur={handleVendorBlur}
                  onChange={(e) => {
                    setVendorSearchText(e.target.value);
                    setVendorDropdownOpen(true);
                    if (vendorNameError) setVendorNameError(false);
                    if (vendorName && e.target.value !== vendorName) {
                      setVendorName("");
                      setInstallerName("");
                      setInstallerSearchText("");
                      setInstallerList([]);
                    }
                  }}
                />
                <SearchableArrow isOpen={vendorDropdownOpen}>&#9662;</SearchableArrow>
              </SearchableInputContainer>
              {vendorNameError && <ErrorText>Please Select Vendor Name</ErrorText>}
              {vendorDropdownOpen &&
                renderDropdownMenu({
                  items: filteredVendors,
                  searchText: vendorSearchText,
                  selectedName: vendorName,
                  nameKey: "vendorName",
                  idKey: "vendorId",
                  emptyMessage: "No vendors found",
                  onSelect: handleVendorSelect,
                })}
            </SearchableSelectWrapper>

            <SearchableSelectWrapper ref={installerWrapperRef}>
              <Body size="large" primitive="span" bold css={{ marginBottom: "4px", display: "block" }}>
                Installer Name<RequiredIndicator aria-hidden="true">*</RequiredIndicator>
              </Body>
              <SearchableInputContainer hasError={installerNameError} disabled={!vendorName}>
                <SearchableInput
                  type="text"
                  data-testid="installer-name"
                  aria-required="true"
                  placeholder="Type to search installer..."
                  value={installerSearchText}
                  disabled={!vendorName}
                  onFocus={() => setInstallerDropdownOpen(true)}
                  onBlur={handleInstallerBlur}
                  onChange={(e) => {
                    setInstallerSearchText(e.target.value);
                    setInstallerDropdownOpen(true);
                    if (installerNameError) setInstallerNameError(false);
                    if (installerName && e.target.value !== installerName) {
                      setInstallerName("");
                    }
                  }}
                />
                <SearchableArrow isOpen={installerDropdownOpen}>&#9662;</SearchableArrow>
              </SearchableInputContainer>
              {installerNameError && <ErrorText>Please Select Installer Name</ErrorText>}
              {installerDropdownOpen &&
                vendorName &&
                renderDropdownMenu({
                  items: filteredInstallers,
                  searchText: installerSearchText,
                  selectedName: installerName,
                  nameKey: "installerName",
                  idKey: "id",
                  emptyMessage: "No installers found",
                  onSelect: handleInstallerSelect,
                })}
            </SearchableSelectWrapper>
            {contractFlexFlag && (
              <>
                <FlexRow>
                  <SLDropDown
                    disabled={!contractFlexFlag}
                    value={reason}
                    label="Swap Reason"
                    type="text"
                    width="305%"
                    name="reason"
                    onChange={handleReasonChange}
                    style={{ fontSize: "1em" }}
                    error={reasonError}
                    errorText="Please Select Reason"
                  >
                    <option disabled selected hidden value=""></option>
                    <option value="Repair">Repair</option>
                    <option value="Replace">Replacement</option>
                  </SLDropDown>
                </FlexRow>

                <FlexRow>
                  <SLDropDown
                    disabled={!contractFlexFlag}
                    value={installType}
                    label="Install Type"
                    type="text"
                    width="260%"
                    name="installType"
                    onChange={handleInstallTypeChange}
                    style={{ fontSize: "1em" }}
                    error={installTypeError}
                    errorText="Please Select Install Type"
                  >
                    <option disabled selected hidden value=""></option>
                    <option value="VMI">VMI</option>
                    <option value="CMI">CMI</option>
                  </SLDropDown>
                </FlexRow>
              </>
            )}
            <ButtonFlexWrapper>
              <SLButton onClick={searchSalesforceCases}>Search Salesforce Cases</SLButton>
              <SLButton onClick={swapESN}>Swap ESN</SLButton>
              <SLButton disabled>Swap ESN & Add Peripherals</SLButton>
              <SLButton onClick={localSwap} disabled>
                Local Swap
              </SLButton>
            </ButtonFlexWrapper>
          </CompactCol>
          <CompactCol>
            <br />
            {oldESNNotification && (
              <NotificationWrapper key={generate()} type="error" title={`Cases for Old ESN ${oldESN} not found`} fullBleed={true} />
            )}
            {(cases?.length > 0 || oldESNCases.length > 0) && (
              <Spacer top="20px" bottom="40px">
                <GridComponent
                  css={{ padding: "50px 0px" }}
                  noDataMessage="No data found"
                  rows={cases?.length > 0 ? cases : oldESNCases}
                  columns={gridConfig}
                  title={"SF Cases for Old ESN " + oldESN}
                />
              </Spacer>
            )}
            <br />
            {newESNNotification && (
              <NotificationWrapper key={generate()} type="error" title={`Cases for New ESN ${newESN} not found`} fullBleed={true} />
            )}
            {newESNCases.length > 0 && (
              <GridComponent
                css={{ padding: "50px 0px" }}
                noDataMessage="No data found"
                rows={newESNCases}
                columns={gridConfig}
                title={"SF Cases for New ESN " + newESN}
              />
            )}
          </CompactCol>
        </SLRowLayout>
      </SLGridLayout>
    </>
  );
}

export const SwapDevicesMetadata = {
  name: "Swap Devices Confirmation",
  id: "swap_devices_final",
  component: lazy(() => import("./SwapDevicesFinal")),
  route: "/swap_devices",
};
